import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowLeft, ArrowRight, AlertCircle, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/auth";

export default function AdminResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState({ type: "idle", message: "" });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      setStatus({ type: "error", message: "Password must be at least 8 characters." });
      return;
    }
    if (password !== confirm) {
      setStatus({ type: "error", message: "Passwords do not match." });
      return;
    }
    setStatus({ type: "loading", message: "" });
    try {
      await api.post("/admin/reset-password", { token, new_password: password });
      setStatus({ type: "done", message: "" });
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      setStatus({ type: "error", message: msg });
    }
  };

  return (
    <div className="min-h-screen bg-[#F4FAF8] flex items-center justify-center px-4">
      <div className="absolute inset-0 -z-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#5BAE71]/15 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#E8A33D]/15 blur-3xl" />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        <div className="flex items-center justify-center gap-2.5 mb-7">
          <img
            src="https://customer-assets.emergentagent.com/job_design-upgrade-217/artifacts/fzqlgxrv_App%20icon.png"
            alt="Careable"
            className="w-12 h-12 rounded-2xl object-cover"
          />
          <div className="leading-tight">
            <p className="font-display font-extrabold text-[#1F4E5F] text-xl">Careable</p>
            <p className="text-[10px] font-bold tracking-[0.18em] text-[#E8A33D] uppercase">
              Admin Console
            </p>
          </div>
        </div>

        <div className="bg-white border border-[#C9DDD6] rounded-3xl p-7 sm:p-9 shadow-[0_18px_50px_rgba(15,56,32,0.05)]">
          {!token ? (
            <div data-testid="reset-no-token" className="text-center space-y-3">
              <div className="w-14 h-14 rounded-2xl bg-[#FBEFD9] flex items-center justify-center mx-auto">
                <AlertCircle className="w-7 h-7 text-[#B27818]" />
              </div>
              <h1 className="font-display text-xl font-bold text-[#0E2A36]">Invalid reset link</h1>
              <p className="text-sm text-[#48606B]">
                This link is missing its security token. Please request a new reset email.
              </p>
              <Link
                to="/admin/forgot-password"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1F4E5F]"
              >
                Request a new link <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : status.type === "done" ? (
            <div data-testid="reset-done" className="text-center space-y-3">
              <div className="w-14 h-14 rounded-2xl bg-[#E1F0E7] flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-7 h-7 text-[#2E7D52]" />
              </div>
              <h1 className="font-display text-2xl font-bold text-[#0E2A36]">Password updated</h1>
              <p className="text-sm text-[#48606B]">
                Your password has been changed. You can now sign in with your new password.
              </p>
              <Link
                to="/admin/login"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1F4E5F] hover:gap-2.5 transition-all"
              >
                Go to sign in <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} data-testid="reset-form" className="space-y-4">
              <div>
                <h1 className="font-display text-2xl font-bold text-[#0E2A36]">
                  Set a new password
                </h1>
                <p className="text-sm text-[#6A8290] mt-1">
                  Choose a strong password (at least 8 characters).
                </p>
              </div>

              <PwField
                id="rp-new"
                label="New password"
                testid="reset-new-password"
                value={password}
                onChange={setPassword}
              />
              <PwField
                id="rp-confirm"
                label="Confirm new password"
                testid="reset-confirm-password"
                value={confirm}
                onChange={setConfirm}
              />

              {status.type === "error" && (
                <div
                  data-testid="reset-error"
                  className="flex items-center gap-2 text-[#C84444] bg-[#E55B5B]/10 border border-[#E55B5B]/25 rounded-xl px-3 py-2.5 text-sm"
                >
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{status.message}</span>
                </div>
              )}

              <button
                type="submit"
                data-testid="reset-submit"
                disabled={status.type === "loading"}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#1F4E5F] hover:bg-[#173E50] text-[#F4FAF8] px-6 py-3.5 rounded-full text-[15px] font-semibold transition-all hover:gap-3 disabled:opacity-70"
              >
                {status.type === "loading" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Updating…
                  </>
                ) : (
                  <>
                    Update password <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <div className="text-center">
                <Link
                  to="/admin/login"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#6A8290] hover:text-[#1F4E5F] transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" /> Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}

function PwField({ id, label, testid, value, onChange }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-xs font-bold uppercase tracking-[0.14em] text-[#3D5560] mb-2"
      >
        {label}
      </label>
      <div className="relative">
        <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6A8290]" />
        <input
          id={id}
          type="password"
          data-testid={testid}
          autoComplete="new-password"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="••••••••"
          className="w-full pl-10 pr-4 py-3 bg-[#F4FAF8] border border-[#C9DDD6] rounded-xl text-[15px] focus:outline-none focus:border-[#1F4E5F] focus:ring-2 focus:ring-[#5BAE71]/30 transition"
        />
      </div>
    </div>
  );
}
